import { useNavigate } from "react-router-dom";
import { FaUserTie, FaBuilding, FaGlobeAmericas, FaFileSignature, FaMoneyBillWave, FaCheck, FaTimes } from "react-icons/fa";
import "./GettingStartedShared.css";

const General2 = () => {
    const navigate = useNavigate();

    const relatedArticles = [
        {
            title: "How the USA Incorporation Process Works",
            description: "A step-by-step guide to incorporating your business in the United States.",
            link: "/how-the-usa-incorporation-process-word"
        },
        {
            title: "6 reasons why your startup should be incorporated in the US",
            description: "Discover the key benefits of incorporating your startup in the United States.",
            link: "/why-should-your-startup-incorporate-in-the-usa"
        },
        {
            title: "What Are the Ongoing Compliance Requirements After US Incorporation?",
            description: "Understand the legal and tax obligations for maintaining your US business entity.",
            link: "/what-are-the-ongoing-compliance-requirements-after-us-incorporation?"
        }
    ];

    return (
        <div className="gs-container">
            <header className="gs-header">
                <div className="gs-container-1200">
                </div>
            </header>
            
            <main className="gs-main-content">
                <div className="gs-container-1200">
                    <nav className="gs-breadcrumb">
                        <ol>
                            <li><a href="/help">Help Center</a></li>
                            <li><a href="/getting-started">Getting Started</a></li>
                            <li>General</li>
                        </ol>
                    </nav>

                    <article className="gs-article">
                        <header className="gs-article-header">
                            <h1 className="gs-article-title">Can a Non-US Resident or Non-US Citizen Incorporate in the US?</h1>
                            <div className="gs-article-meta">
                                <span>Posted by BooksComply Team</span>
                                <span>•</span>
                                <span>Last updated August 2025</span>
                            </div>
                        </header>
                        
                        <div className="gs-article-content">
                            <p className="gs-lead">
                                Yes! Non-US residents and non-US citizens can legally incorporate a business in the United States without needing to live in the country or hold a visa. The U.S. is one of the most business-friendly nations, allowing foreigners to start and operate companies easily.
                            </p>

                            <h2>What Business Structures Can Non-US Residents Form?</h2>
                            <p>
                                As a non-US resident, you can choose between:
                            </p>
                            <ul className="gs-tips-list">
                                <li>
                                    <span className="gs-icon-text">
                                        <FaCheck className="gs-icon check" />
                                        <span><strong>LLC (Limited Liability Company)</strong> – A flexible structure with minimal compliance requirements. Best for small to medium businesses.</span>
                                    </span>
                                </li>
                                <li>
                                    <span className="gs-icon-text">
                                        <FaCheck className="gs-icon check" />
                                        <span><strong>C-Corporation (C-Corp)</strong> – A preferred option for companies planning to raise funds from investors or operate internationally.</span>
                                    </span>
                                </li>
                                <li>
                                    <span className="gs-icon-text">
                                        <FaTimes className="gs-icon times" />
                                        <span><strong>S-Corporations</strong> are NOT available to non-US residents, as they require all shareholders to be U.S. citizens or permanent residents.</span>
                                    </span>
                                </li>
                            </ul>

                            <h2>Which U.S. State Should You Incorporate In?</h2>
                            <p>
                                You can incorporate in any state, but popular choices for non-US residents include:
                            </p>
                            <ul className="gs-tips-list">
                                <li>
                                    <span className="gs-icon-text">
                                        <FaCheck className="gs-icon check" />
                                        <span><strong>Delaware</strong> – Business-friendly laws, no sales tax, and privacy benefits.</span>
                                    </span>
                                </li>
                                <li>
                                    <span className="gs-icon-text">
                                        <FaCheck className="gs-icon check" />
                                        <span><strong>Wyoming</strong> – No state income tax, strong asset protection, and low fees.</span>
                                    </span>
                                </li>
                                <li>
                                    <span className="gs-icon-text">
                                        <FaCheck className="gs-icon check" />
                                        <span><strong>Nevada</strong> – No corporate tax and enhanced business privacy.</span>
                                    </span>
                                </li>
                                <li>
                                    <span className="gs-icon-text">
                                        <FaCheck className="gs-icon check" />
                                        <span><strong>Your U.S. Business Location</strong> – If you operate in a specific state, it may be best to register there.</span>
                                    </span>
                                </li>
                            </ul>

                            <h2>Steps to Incorporate as a Non-US Resident</h2>
                            <ol className="gs-tips-list">
                                <li>Choose a Business Structure (LLC or C-Corp).</li>
                                <li>Select a State for Incorporation based on your business needs.</li>
                                <li>Register Your Business Name and file the required incorporation documents.</li>
                                <li>Appoint a Registered Agent (a legal requirement for foreign business owners).</li>
                                <li>Obtain an EIN (Employer Identification Number) from the IRS (required for tax and banking purposes).</li>
                                <li>Open a U.S. Business Bank Account</li>
                                <li>Comply with Ongoing U.S. Tax & Legal Requirements (annual filings, taxes, and licenses).</li>
                            </ol>

                            <h2>Do You Need a U.S. Address or SSN to Incorporate?</h2>
                            <p>
                                <b>No, you do NOT need a Social Security Number (SSN) or U.S. address to incorporate.</b> However, you'll need:
                            </p>
                            <ul className="gs-tips-list">
                                <li>A Registered Agent in the U.S.</li>
                                <li>An EIN for tax purposes</li>
                            </ul>

                            <h2>Can a Non-US Resident Open a U.S. Bank Account?</h2>
                            <p>
                                Yes, but the process can be complex. Some banks require in-person visits, while others allow remote account opening.
                            </p>

                            <h2>What About U.S. Taxes for Foreign-Owned Companies?</h2>
                            <ul className="gs-tips-list">
                                <li><b>LLCs:</b> Profits are taxed based on U.S. income and applicable treaties with your home country.</li>
                                <li><b>C-Corporations:</b> Subject to U.S. corporate tax, but foreign owners only pay taxes on U.S.-sourced income.</li>
                            </ul>

                            <h2>Requirements for Non-US Residents to Incorporate in the US</h2>
                            <p>
                                While the process is straightforward, you'll need to meet these requirements:
                            </p>
                            <div className="gs-feature-grid">
                                <div className="gs-feature-card">
                                    <div className="gs-feature-icon">
                                        <FaUserTie />
                                    </div>
                                    <h3>Registered Agent</h3>
                                    <p>You must appoint a registered agent with a physical address in the state of incorporation.</p>
                                </div>
                                <div className="gs-feature-card">
                                    <div className="gs-feature-icon">
                                        <FaBuilding />
                                    </div>
                                    <h3>US Address</h3>
                                    <p>A US mailing address is required for official correspondence and business operations.</p>
                                </div>
                                <div className="gs-feature-card">
                                    <div className="gs-feature-icon">
                                        <FaMoneyBillWave />
                                    </div>
                                    <h3>US Bank Account</h3>
                                    <p>While not mandatory for formation, you'll need one to receive payments and pay US taxes.</p>
                                </div>
                                <div className="gs-feature-card">
                                    <div className="gs-feature-icon">
                                        <FaFileSignature />
                                    </div>
                                    <h3>ITIN or EIN</h3>
                                    <p>An Individual Taxpayer Identification Number (ITIN) or Employer Identification Number (EIN) is required for tax purposes.</p>
                                </div>
                            </div>

                            <h2>Tax Considerations for Non-US Business Owners</h2>
                            <p>
                                Understanding US tax obligations is crucial for international business owners:
                            </p>
                            <ul className="gs-tips-list">
                                <li>LLCs are typically treated as pass-through entities for tax purposes.</li>
                                <li>C-Corps are subject to corporate income tax at the federal and state levels.</li>
                                <li>Non-resident owners may be subject to withholding taxes on certain types of US-sourced income.</li>
                                <li>Tax treaties between the US and your home country may affect your tax liability.</li>
                            </ul>
                            <div className="gs-note">
                                <h4>Important Note:</h4>
                                <p>We recommend consulting with a tax professional to understand your specific tax obligations based on your country of residence and business activities.</p>
                            </div>

                            <h2>How We Help Non-US Residents</h2>
                            <p>
                                Our firm specializes in helping international entrepreneurs navigate the US business landscape:
                            </p>
                            <div className="gs-feature-grid">
                                <div className="gs-feature-card">
                                    <div className="gs-feature-icon">
                                        <FaGlobeAmericas />
                                    </div>
                                    <h3>Business Structure</h3>
                                    <p>Help you choose the right business structure for your international operations.</p>
                                </div>
                                <div className="gs-feature-card">
                                    <div className="gs-feature-icon">
                                        <FaFileSignature />
                                    </div>
                                    <h3>Documentation</h3>
                                    <p>Handle all incorporation paperwork and state filings efficiently.</p>
                                </div>
                                <div className="gs-feature-card">
                                    <div className="gs-feature-icon">
                                        <FaBuilding />
                                    </div>
                                    <h3>Business Setup</h3>
                                    <p>Assist with EIN acquisition and US business address services.</p>
                                </div>
                                <div className="gs-feature-card">
                                    <div className="gs-feature-icon">
                                        <FaMoneyBillWave />
                                    </div>
                                    <h3>Banking</h3>
                                    <p>Guide you through setting up a US business bank account remotely.</p>
                                </div>
                            </div>
                            
                            <div className="gs-note">
                                <h4>Important Note:</h4>
                                <p>While we can help with the business formation process, we don't provide immigration or visa services. Our services are focused on business incorporation and compliance.</p>
                            </div>
                        </div>
                    </article>
                    
                    {/* <div className="gs-helpful-section">
                        <h3 className="gs-helpful-title">Was this article helpful?</h3>
                        <div className="gs-helpful-buttons">
                            <button className="gs-helpful-btn positive">
                                <FaCheckCircle /> Yes, it was helpful
                            </button>
                            <button className="gs-helpful-btn negative">
                                <FaTimesCircle /> No, I need more help
                            </button>
                        </div>
                    </div> */}
                    
                    <div className="gs-related-articles">
                        <h2 className="gs-related-title">Related Articles</h2>
                        <div className="gs-related-grid">
                            {relatedArticles.map((article, index) => (
                                <a 
                                    key={index} 
                                    href={article.link} 
                                    className="gs-related-card"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate(article.link);
                                    }}
                                >
                                    <h3>{article.title}</h3>
                                    <p>{article.description}</p>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default General2;